"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle, AlertCircle } from "lucide-react";

interface LeadFormData {
  full_name: string;
  phone_number: string;
  brand_name: string;
  insta_handle: string;
  email: string;
}

interface FormErrors {
  full_name?: string;
  phone_number?: string;
  email?: string;
}

export function ContactFormWrapper() {
  const [formData, setFormData] = useState<LeadFormData>({
    full_name: "",
    phone_number: "",
    brand_name: "",
    insta_handle: "",
    email: "",
  });
  const [errors, setFormErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validation functions
  const validateName = (name: string): string | undefined => {
    if (!name.trim()) return "Full name is required";
    if (name.trim().length < 2) return "Name must be at least 2 characters";
    if (name.trim().length > 50) return "Name must be less than 50 characters";
    if (!/^[a-zA-Z\s\-']+$/.test(name.trim())) {
      return "Name can only contain letters, spaces, hyphens, and apostrophes";
    }
    return undefined;
  };

  const validatePhoneNumber = (phone: string): string | undefined => {
    if (!phone.trim()) return "Phone number is required";
    const cleanPhone = phone.replace(/[\s\-]/g, "");
    if (!cleanPhone.startsWith("+")) {
      return "Phone number must start with country code (e.g., +91, +1)";
    }
    const countryCodeMatch = cleanPhone.match(/^\+\d{1,4}/);
    if (!countryCodeMatch) {
      return "Invalid country code format";
    }
    if (cleanPhone.length < 8 || cleanPhone.length > 16) {
      return "Phone number must be 7-15 digits including country code";
    }
    if (!/^\+\d+$/.test(cleanPhone)) {
      return "Phone number can only contain digits after the + symbol";
    }
    return undefined;
  };

  const validateEmail = (email: string): string | undefined => {
    if (!email.trim()) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return "Please enter a valid email address";
    }
    if (email.trim().length > 254) {
      return "Email address is too long";
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    const nameError = validateName(formData.full_name);
    if (nameError) newErrors.full_name = nameError;
    const phoneError = validatePhoneNumber(formData.phone_number);
    if (phoneError) newErrors.phone_number = phoneError;
    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof LeadFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const cloudFunctionData = {
        full_name: formData.full_name.trim(),
        email: formData.email.trim(),
        brand_name: formData.brand_name.trim(),
        insta_handle: formData.insta_handle.trim(),
        phone_number: formData.phone_number.trim(),
        referral_source: "www.photozoot.in",
      };

      const response = await fetch("/api/join-waitlist", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(cloudFunctionData),
      });

      if (!response.ok) {
        let errorMessage = "Failed to submit form. Please try again.";
        switch (response.status) {
          case 400:
            errorMessage = "Invalid data provided. Please check your information and try again.";
            break;
          case 409:
            errorMessage = "You may have already joined our waitlist with this email or phone number.";
            break;
          case 429:
            errorMessage = "Too many requests. Please try again later.";
            break;
          case 500:
            errorMessage = "Server error. Please try again later.";
            break;
        }
        throw new Error(errorMessage);
      }

      setShowSuccess(true);
      setFormData({
        full_name: "",
        phone_number: "",
        brand_name: "",
        insta_handle: "",
        email: "",
      });
      setFormErrors({});
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unexpected error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="text-center py-8">
        <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold mb-2">Thank You!</h3>
        <p className="text-muted-foreground mb-4">
          You&apos;ve successfully joined our waitlist. We&apos;ll be in touch within 24 hours.
        </p>
        <Button
          onClick={() => setShowSuccess(false)}
          variant="outline"
          className="mx-auto"
        >
          Submit Another Response
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold mb-2">Get Started with PhotoZoot</h3>
        <p className="text-muted-foreground">
          Join our waitlist and be among the first to experience the future of jewelry photography.
        </p>
      </div>

      {/* Full Name */}
      <fieldset className="space-y-2">
        <Label htmlFor="full_name" className="text-sm font-medium">
          Full Name *
        </Label>
        <Input
          id="full_name"
          type="text"
          required
          value={formData.full_name}
          onChange={(e) => handleInputChange("full_name", e.target.value)}
          placeholder="Enter your full name"
          className={`w-full ${
            errors.full_name ? "border-red-500 focus:border-red-500" : ""
          }`}
          disabled={isSubmitting}
          aria-required="true"
          aria-invalid={!!errors.full_name}
          aria-describedby={errors.full_name ? "name-error" : undefined}
        />
        {errors.full_name && (
          <div className="flex items-center gap-2 text-sm text-red-600">
            <AlertCircle className="h-4 w-4" />
            <span id="name-error">{errors.full_name}</span>
          </div>
        )}
      </fieldset>

      {/* Phone Number */}
      <fieldset className="space-y-2">
        <Label htmlFor="phone_number" className="text-sm font-medium">
          Contact Number *
        </Label>
        <Input
          id="phone_number"
          type="tel"
          required
          value={formData.phone_number}
          onChange={(e) => handleInputChange("phone_number", e.target.value)}
          placeholder="+91 98765 43210"
          className={`w-full ${
            errors.phone_number ? "border-red-500 focus:border-red-500" : ""
          }`}
          disabled={isSubmitting}
          aria-required="true"
          aria-invalid={!!errors.phone_number}
          aria-describedby={errors.phone_number ? "phone-error" : "phone-hint"}
        />
        <p id="phone-hint" className="text-xs text-muted-foreground">
          Start with country code (e.g., +91 for India, +1 for US)
        </p>
        {errors.phone_number && (
          <div className="flex items-center gap-2 text-sm text-red-600">
            <AlertCircle className="h-4 w-4" />
            <span id="phone-error">{errors.phone_number}</span>
          </div>
        )}
      </fieldset>

      {/* Email */}
      <fieldset className="space-y-2">
        <Label htmlFor="email" className="text-sm font-medium">
          Email Address *
        </Label>
        <Input
          id="email"
          type="email"
          required
          value={formData.email}
          onChange={(e) => handleInputChange("email", e.target.value)}
          placeholder="Enter your email address"
          className={`w-full ${
            errors.email ? "border-red-500 focus:border-red-500" : ""
          }`}
          disabled={isSubmitting}
          aria-required="true"
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? "email-error" : undefined}
        />
        {errors.email && (
          <div className="flex items-center gap-2 text-sm text-red-600">
            <AlertCircle className="h-4 w-4" />
            <span id="email-error">{errors.email}</span>
          </div>
        )}
      </fieldset>

      {/* Brand Name */}
      <fieldset className="space-y-2">
        <Label htmlFor="brand_name" className="text-sm font-medium">
          Brand/Business Name *
        </Label>
        <Input
          id="brand_name"
          type="text"
          required
          value={formData.brand_name}
          onChange={(e) => handleInputChange("brand_name", e.target.value)}
          placeholder="Enter your brand name"
          className="w-full"
          disabled={isSubmitting}
          aria-required="true"
        />
      </fieldset>

      {/* Instagram Handle */}
      <fieldset className="space-y-2">
        <Label htmlFor="insta_handle" className="text-sm font-medium">
          Instagram Handle
        </Label>
        <Input
          id="insta_handle"
          type="text"
          value={formData.insta_handle}
          onChange={(e) => handleInputChange("insta_handle", e.target.value)}
          placeholder="@yourhandle (optional)"
          className="w-full"
          disabled={isSubmitting}
        />
      </fieldset>

      {/* General Error Message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
        aria-describedby={isSubmitting ? "submitting-status" : undefined}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            <span id="submitting-status">Submitting...</span>
          </>
        ) : (
          "Submit & Join Waitlist"
        )}
      </Button>

      {/* Privacy Notice */}
      <p className="text-xs text-muted-foreground text-center">
        By submitting this form, you agree to our privacy policy and
        consent to being contacted about our services.
      </p>
    </form>
  );
}
